import { Flex, Text } from '@chakra-ui/react';

import { Badge, BadgeStatus, PayCycle, safeStringFormatDate } from '@aslanpay/portal-common-lib';

import { getPayCycleStage, PayCycleStage } from 'app/utils/payCycles';

export const PAY_CYCLE_STAGE_BADGES: Record<PayCycleStage, BadgeStatus> = {
  UPCOMING: 'created',
  CURRENT: 'invited',
  CUT_OFF: 'warning',
  PAST: 'locked',
};

type PayCycleSummaryProps = {
  payCycle?: Pick<PayCycle, 'period' | 'startDate' | 'endDate' | 'cutoffDate' | 'payday'>;
  showDates?: boolean;
  today?: Date;
};

/**
 * @description Formats a date for a pay cycle, short within the year it falls in.
 */
const formatDay = (date?: string, withYear = false) =>
  date ? safeStringFormatDate(date, { formatString: withYear ? 'd MMM yyyy' : 'd MMM' }) : '-';

/**
 * @description Names the badge for a pay cycle's stage.
 */
export const getPayCycleStageLabel = (stage: PayCycleStage, payday?: string) => {
  switch (stage) {
    case 'UPCOMING':
      return 'Upcoming';
    case 'CURRENT':
      return 'Current';
    case 'CUT_OFF':
      return `Cut off · pays ${formatDay(payday)}`;
    default:
      return 'Past';
  }
};

/** Shows a pay cycle as its period, where it is relative to today, and its dates. */
function PayCycleSummary({
  payCycle = undefined,
  showDates = true,
  today = undefined,
}: PayCycleSummaryProps) {
  if (!payCycle) {
    return <Text>-</Text>;
  }

  const stage = getPayCycleStage(payCycle, today);
  const label = stage && getPayCycleStageLabel(stage, payCycle.payday);

  return (
    <Flex flexDir="column" gap="4px" data-testid="pay-cycle-summary">
      <Flex align="center" gap="8px">
        <Text color={stage === 'PAST' ? 'brand.darkGray' : 'brand.black'}>
          Period {payCycle.period}
        </Text>
        {label && stage !== 'PAST' && (
          <Badge value={label} status={PAY_CYCLE_STAGE_BADGES[stage]} />
        )}
      </Flex>
      {showDates && (
        <Text textStyle="extraSmallRegular" color="brand.darkGray" whiteSpace="nowrap">
          {`${formatDay(payCycle.startDate)} – ${formatDay(
            payCycle.endDate,
            true
          )} · cut-off ${formatDay(payCycle.cutoffDate)} · payday ${formatDay(payCycle.payday)}`}
        </Text>
      )}
    </Flex>
  );
}

export default PayCycleSummary;
